import {
  Cache,
  WebGLRenderer,
  PerspectiveCamera,
  Scene as SceneThree,
  Vector3,
  Raycaster,
  TextureLoader,
} from "three";

import {
  Zone,
  ZoneDataEvent,
  ZoneOverEvent,
  ZoneParams,
  BasicStyle,
  LineStyle,
  LabelStyle,
  Coordinates,
  ZoneCoords,
} from "./zone.class";

import { Sprite, SpriteParams } from "./sprite.class";

import {
  ImageProvider,
  ImageSequenceDefinition,
  Frame,
} from "./imageProvider.class";

import { getEventCoords } from "./utils";

type RawCoordinates = [number, number, number, number, number, number];

interface BasicShapeStyleDefinition extends BasicStyle {
  textureUrl?: string;
}

interface ShapeStyleDefinition extends BasicShapeStyleDefinition {
  active?: {
    opacity?: number;
    blink?: string;
  };
}

interface ZoneStyleDefinition extends ShapeStyleDefinition {
  line?: LineStyle | boolean;
  label?: LabelStyle | boolean;
}

interface SceneDefinition extends ImageSequenceDefinition {
  frames: RawCoordinates[];
  fov: number;
  zones: ZoneParams[];
  sprites: SpriteParams[];
}

Cache.enabled = true;

type Handlers = {
  onZoneLeave?: (event?: ZoneOverEvent) => void;
  onZoneEnter?: (event: ZoneOverEvent) => void;
  onZoneFollow?: (event: ZoneOverEvent) => void;
  onZoneClick?: (event: ZoneOverEvent) => void;
  onClickOut?: () => void;
};

class SceneRenderer {
  static textureLoader: TextureLoader = new TextureLoader();
  readonly renderer: WebGLRenderer;
  readonly camera: PerspectiveCamera;
  readonly scene: SceneThree;
  readonly canvas: HTMLCanvasElement;
  readonly ctx2d: CanvasRenderingContext2D;
  private raycaster: Raycaster;
  private imageProvider: ImageProvider;
  private fps: number = 60;
  private angle: number = 0;
  private needUpdate: boolean = false;
  private frames: { position: Coordinates; rotation: Coordinates }[];
  private frameIndex: number = 0;
  private zones: { [key: string]: Zone } = {};
  private sprites: { [key: string]: Sprite } = {};
  private hovered: ZoneOverEvent;
  private following: ZoneOverEvent;
  private handlers: Handlers = {};

  constructor() {
    Zone.textureLoader = SceneRenderer.textureLoader;
    Sprite.textureLoader = SceneRenderer.textureLoader;
    this.imageProvider = new ImageProvider();
    this.imageProvider.setHdReadyHandler(this.onHdReady.bind(this));
    this.angle = 0;
    this.renderer = new WebGLRenderer({ antialias: true, alpha: true });
    this.raycaster = new Raycaster();
    this.camera = new PerspectiveCamera(50, 16 / 9, 0.1, 10000);
    this.camera.rotation.order = "ZYX";
    this.scene = new SceneThree();
    this.canvas = document.createElement("canvas");
    this.canvas.addEventListener("mousemove", this.onMouseMove.bind(this));
    this.canvas.addEventListener("click", this.onMouseClick.bind(this));
    this.ctx2d = this.canvas.getContext("2d");

    this.tick = this.tick.bind(this);
    this.tick();
  }

  setHandler(name: string, handler: (event?: ZoneOverEvent) => void) {
    this.handlers[name as keyof Handlers] = handler;
  }

  getAngle() {
    return this.angle;
  }

  onHdReady(frame: Frame, index: number) {
    if (index !== this.frameIndex) return;
    const { img, alpha } = frame;
    if (alpha) {
      this.composeWithAlphas(img, alpha);
    } else {
      this.composeNoAlpha(img);
    }
  }

  async setScene(scene: SceneDefinition) {
    this.clearScene();
    this.setFrames(scene.frames);
    this.fps = scene.fps;
    this.camera.fov = scene.fov;
    this.camera.updateProjectionMatrix();
    if (this.following) {
      this.handlers.onZoneLeave?.(this.following);
      this.following = null;
      this.hovered = null;
    }

    if (scene.zones) {
      await this.setZones(scene.zones);
    }
    if (scene.sprites?.length) {
      await this.setSprites(scene.sprites);
    }
    this.frameIndex = this.getFrameIndexFromAngle();
    await this.imageProvider.setScene(
      scene as ImageSequenceDefinition,
      this.frameIndex,
      Math.ceil(this.fps / 4) // Nombre d'images à précharger pour pouvoir jouer le rotate
    );
    this.needUpdate = true;
    return true;
  }

  setFrames(frames: RawCoordinates[]) {
    const pi180 = Math.PI / 180;
    this.frames = frames.map((f) => ({
      position: [f[0], f[1], f[2]],
      rotation: [(90 - f[3]) * pi180, f[4] * pi180, -1 * f[5] * pi180],
    }));
  }

  getNearestFrameForZone(key: string) {
    const zone = this.zones[key];
    if (!zone) {
      return;
    }
    const barycentre = zone.baryvector;
    const distances = this.frames.map(({ position }) => {
      const dx = barycentre.x - position[0];
      const dy = barycentre.y - position[1];
      const dz = barycentre.z - position[2];
      return dx * dx + dy * dy + dz * dz;
    });
    // index plus petite distance
    var lowest = 0;
    for (var i = 1; i < distances.length; i++) {
      if (distances[i] < distances[lowest]) lowest = i;
    }
    return (360 * lowest) / this.fps;
  }

  resize({ width, height }: { width: number; height: number }) {
    const elt = this.canvas;
    this.renderer.setSize(width, height);
    elt.style.width = `${width}px`;
    elt.style.height = `${height}px`;
    elt.width = width;
    elt.height = height;
    this.needUpdate = true;
  }

  setAngle(angle: number) {
    this.angle = angle;
  }

  getFrameIndexFromAngle(): number {
    const fps = this.fps;
    const angleStep = 360 / fps;
    const frame = ((Math.floor(this.angle / angleStep) % fps) + fps) % fps;
    return frame;
  }

  updateCamera() {
    if (!this.frames) return;
    const frame = this.frames[this.frameIndex];
    this.camera.position.set(...frame.position);
    this.camera.rotation.set(...frame.rotation);
    this.camera.updateProjectionMatrix();
  }

  clearScene() {
    const scene = this.scene;
    while (scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }
  }

  selectZone(event: any, zone: Zone) {
    const newEvent: ZoneOverEvent = {
      event,
      data: this.getZoneDataEvent(zone),
    };
    if (this.hovered) {
      this.handlers.onZoneLeave?.(this.hovered);
    }
    this.handlers.onZoneEnter?.(newEvent);
    this.hovered = newEvent;
    this.following = newEvent;
  }

  unfollow() {
    this.following = null;
  }

  selectZoneByKey(key: any) {
    const zone = this.zones[key];
    const newEvent: ZoneOverEvent = {
      event: null,
      data: this.getZoneDataEvent(zone),
    };
    if (this.hovered) {
      this.handlers.onZoneLeave?.(this.hovered);
    }
    this.handlers.onZoneEnter?.(newEvent);
    this.hovered = newEvent;
    this.following = newEvent;
  }

  getPointerEventInfos(evt: any) {
    const formattedEvent = getEventCoords(evt);
    const elt = this.canvas;
    const boundingRect = elt.getBoundingClientRect();
    const width = elt.width;
    const height = elt.height;
    let x =
      (formattedEvent.x - boundingRect.left) * (width / boundingRect.width);
    let y =
      (formattedEvent.y - boundingRect.top) * (height / boundingRect.height);
    const vector = new Vector3((x / width) * 2 - 1, -(y / height) * 2 + 1, 0.5);
    this.raycaster.setFromCamera(vector.clone(), this.camera);
    let intersects = this.raycaster.intersectObjects(this.scene.children, true);
    const zone = intersects[0]?.object.userData.zone;
    return {
      event: formattedEvent,
      zone,
    };
  }

  onMouseMove(evt: any) {
    const { event, zone } = this.getPointerEventInfos(evt);
    if (this.hovered?.data && this.hovered?.data?.key !== zone?.key) {
      this.handlers.onZoneLeave?.({
        event: event,
        data: this.hovered.data,
      });
      this.hovered = null;
    }
    if (zone && this.hovered?.data?.key !== zone.key) {
      this.selectZone(event, zone);
    }
  }

  onMouseClick(evt: any) {
    const { event, zone } = this.getPointerEventInfos(evt);
    if (zone) {
      this.handlers.onZoneClick?.({
        event,
        data: this.getZoneDataEvent(zone),
      });
    } else {
      this.handlers.onClickOut?.();
    }
  }

  getZoneDataEvent(zone: any): ZoneDataEvent {
    const point = zone.baryvector.clone();
    point.project(this.camera);
    const { x, y } = point;
    const { width, height, left, top } = this.canvas.getBoundingClientRect();
    zone.barycentre = {
      x: (x + 1) * (width / 2),
      y: (-y + 1) * (height / 2),
    };
    return zone;
  }

  setZones(zones: ZoneParams[]) {
    this.zones = {};
    zones.forEach((z) => {
      const zone = new Zone(z, () => (this.needUpdate = true));
      this.zones[z.key] = zone;
      const zoneMeshes = zone.get3d();
      zoneMeshes.forEach((m) => {
        this.scene.add(m);
      });
    });
  }

  setSprites(sprites: SpriteParams[]) {
    this.sprites = {};
    sprites.forEach((s) => {
      const sprite = new Sprite(s, () => (this.needUpdate = true));
      this.sprites[s.key] = sprite;
      this.scene.add(sprite.get3d());
    });
  }

  composeWithAlphas(img: HTMLImageElement, alpha: HTMLImageElement) {
    const { width, height } = this.canvas;
    if (!(width && height)) return;
    const zones = Object.values(this.zones);
    const sprites = Object.values(this.sprites);
    zones.forEach((z) => {
      z.setShapeVisible(true);
      z.setLineVisible(true);
      z.setSpriteVisible(false);
    });
    sprites.forEach((s) => s.setVisible(false));
    this.renderer.render(this.scene, this.camera);
    this.ctx2d.clearRect(0, 0, width, height);
    this.ctx2d.globalCompositeOperation = "source-over";
    this.ctx2d.drawImage(this.renderer.domElement, 0, 0, width, height);
    this.ctx2d.globalCompositeOperation = "destination-out";
    this.ctx2d.drawImage(alpha, 0, 0, width, height);
    this.ctx2d.globalCompositeOperation = "destination-atop";
    this.ctx2d.drawImage(img, 0, 0, width, height);
    zones.forEach((z) => {
      z.setLineVisible(false);
      z.setShapeVisible(false);
      z.setSpriteVisible(true);
    });
    sprites.forEach((s) => s.setVisible(true));
    this.renderer.render(this.scene, this.camera);
    this.ctx2d.globalCompositeOperation = "source-over";
    this.ctx2d.drawImage(this.renderer.domElement, 0, 0, width, height);
  }

  clearCanvas() {
    const { width, height } = this.canvas;
    this.ctx2d.clearRect(0, 0, width, height);
  }

  composeNoAlpha(img: HTMLImageElement) {
    const { width, height } = this.canvas;
    if (!(width && height)) return;
    this.renderer.render(this.scene, this.camera);
    this.ctx2d.clearRect(0, 0, width, height);
    this.ctx2d.globalCompositeOperation = "source-over";
    this.ctx2d.drawImage(img, 0, 0, width, height);
    this.ctx2d.drawImage(this.renderer.domElement, 0, 0, width, height);
  }

  render() {
    const frame = this.imageProvider.getFrame(this.frameIndex);
    if (!frame) return;
    const { img, alpha } = frame;

    if (alpha) {
      this.composeWithAlphas(img, alpha);
    } else {
      this.composeNoAlpha(img);
    }
    this.follow();
    this.needUpdate = false;
  }

  tick() {
    const frameIndex = this.getFrameIndexFromAngle();
    if (frameIndex !== this.frameIndex || this.needUpdate) {
      this.frameIndex = frameIndex;
      this.updateCamera();
      this.render();
    }
    window.requestAnimationFrame(this.tick);
  }

  follow() {
    if (this.following?.data) {
      this.following.data = this.getZoneDataEvent(this.following.data);
      this.handlers.onZoneFollow?.(this.following);
    }
  }
}

export {
  SceneRenderer,
  ZoneStyleDefinition,
  SceneDefinition,
  ZoneParams,
  RawCoordinates,
  Coordinates,
  ZoneCoords,
};
